import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Zap, Plus, Trash2, Mail, Send, Save, Clock } from "lucide-react";

const channelLabel = { email: "E-mail", sms: "SMS", both: "E-mail + SMS" };

function offsetLabel(o) {
  if (o < 0) return `${Math.abs(o)} dia(s) ANTES do vencimento`;
  if (o === 0) return "No DIA do vencimento";
  return `${o} dia(s) APÓS o vencimento`;
}

export default function Automations() {
  const [rules, setRules] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/rules").then((r) => setRules(r.data));
  useEffect(() => { load(); }, []);

  const save = async (rule) => {
    try {
      if (rule.id) await api.put(`/rules/${rule.id}`, rule);
      else await api.post("/rules", rule);
      toast.success("Regra salva");
      setEditing(null); load();
    } catch { toast.error("Erro ao salvar regra"); }
  };
  const del = async (id) => { await api.delete(`/rules/${id}`); toast.success("Regra removida"); load(); };
  const toggle = async (rule) => { await api.put(`/rules/${rule.id}`, { ...rule, enabled: !rule.enabled }); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-100 flex items-center gap-2"><Zap className="h-7 w-7 text-amber-400" /> Automações</h1>
          <p className="text-sm text-slate-400 mt-1">Lembretes automáticos de cobrança por e-mail e SMS</p>
        </div>
        <button data-testid="add-rule-button" onClick={() => setEditing({ name: "", offset_days: -3, channel: "both", enabled: true, email_subject: "", email_body: "", sms_body: "" })}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600">
          <Plus className="h-4 w-4" /> Nova regra
        </button>
      </div>

      <div className="bg-[#111827] border border-[#1F293D] rounded-xl p-4 mb-5 text-xs text-slate-400 flex items-center gap-2">
        <Clock className="h-4 w-4 text-sky-400" />
        As regras rodam automaticamente todo dia no horário configurado. Tags disponíveis: <code className="font-mono text-sky-300">{"{nome} {primeiro_nome} {plano} {valor} {vencimento} {usuario} {senha} {servidor} {link}"}</code>
      </div>

      <div className="space-y-3">
        {rules.map((r) => (
          <div key={r.id} data-testid={`rule-${r.id}`} className="bg-[#111827] border border-[#1F293D] rounded-xl p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button data-testid={`rule-toggle-${r.id}`} onClick={() => toggle(r)}
                  className={`h-6 w-11 rounded-full transition-colors relative ${r.enabled ? "bg-sky-500" : "bg-slate-600"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${r.enabled ? "left-5.5" : "left-0.5"}`} style={{ left: r.enabled ? 22 : 2 }} />
                </button>
                <div>
                  <div className="text-slate-100 font-semibold">{r.name}</div>
                  <div className="text-xs text-slate-400">{offsetLabel(r.offset_days)} · {channelLabel[r.channel]}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`rule-edit-${r.id}`} onClick={() => setEditing(r)} className="px-3 py-1.5 rounded-lg bg-[#1A2234] border border-[#202D45] text-slate-300 text-xs hover:text-white">Editar</button>
                <button data-testid={`rule-delete-${r.id}`} onClick={() => del(r.id)} className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="mt-3 pl-14 text-xs text-slate-500 flex flex-wrap gap-4">
              {r.channel !== "sms" && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5 text-sky-400" /> {r.email_subject || "sem assunto"}</span>}
              {r.channel !== "email" && <span className="flex items-center gap-1"><Send className="h-3.5 w-3.5 text-amber-400" /> {(r.sms_body || "").slice(0, 60) || "sem texto"}</span>}
            </div>
          </div>
        ))}
      </div>

      {editing && <RuleEditor rule={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function RuleEditor({ rule, onClose, onSave }) {
  const [f, setF] = useState(rule);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="rule-editor">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-[#0F141F] border border-[#1F293D] rounded-xl p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-white mb-4">{rule.id ? "Editar regra" : "Nova regra"}</h3>
        <div className="space-y-3">
          <L label="Nome da regra"><input data-testid="rule-name" value={f.name} onChange={set("name")} className={inp} /></L>
          <div className="grid grid-cols-2 gap-3">
            <L label="Quando enviar (dias)">
              <input data-testid="rule-offset" type="number" value={f.offset_days} onChange={(e) => setF({ ...f, offset_days: parseInt(e.target.value || 0) })} className={inp} />
              <span className="text-[11px] text-slate-500 mt-1 block">{offsetLabel(f.offset_days)} (negativo = antes)</span>
            </L>
            <L label="Canal">
              <select data-testid="rule-channel" value={f.channel} onChange={set("channel")} className={inp}>
                <option value="email">E-mail</option>
                <option value="sms">SMS</option>
                <option value="both">E-mail + SMS</option>
              </select>
            </L>
          </div>
          {f.channel !== "sms" && <>
            <L label="Assunto do e-mail"><input data-testid="rule-subject" value={f.email_subject} onChange={set("email_subject")} className={inp} /></L>
            <L label="Corpo do e-mail (HTML)"><textarea data-testid="rule-body" value={f.email_body} onChange={set("email_body")} rows={4} className={inp} /></L>
          </>}
          {f.channel !== "email" && <L label="Texto do SMS (curto)"><textarea data-testid="rule-sms" value={f.sms_body} onChange={set("sms_body")} rows={2} className={inp} />
            <SmsCounter text={f.sms_body} /></L>}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-[#1A2234] border border-[#202D45] text-slate-300 text-sm">Cancelar</button>
          <button data-testid="save-rule-button" onClick={() => onSave(f)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600"><Save className="h-4 w-4" /> Salvar</button>
        </div>
      </div>
    </div>
  );
}

const inp = "mt-1 w-full px-3 py-2 rounded-lg bg-[#111827] border border-[#1F293D] text-sm text-slate-200 focus:border-sky-500 focus:outline-none";

// Estimate with typical values: link ~52 chars, first name ~8, date 10, value ~8
const SAMPLE = { "{primeiro_nome}": "Marcos", "{nome}": "Marcos Silva", "{vencimento}": "04/10/2026", "{valor}": "R$ 25,00",
  "{plano}": "[1 MES] COMPLETO", "{usuario}": "ESJYDH", "{servidor}": "WEPLAY", "{link}": "https://sysslog.cc/#/checkout/lKWOr6eDzo/231qRzxRL4" };

function SmsCounter({ text }) {
  let t = text || "";
  for (const [k, v] of Object.entries(SAMPLE)) t = t.split(k).join(v);
  const n = t.length;
  const accents = /[^\x00-\x7F]/.test(t);
  const limit = accents ? 70 : 160;
  const ok = n <= limit;
  return (
    <span data-testid="sms-counter" className={`text-[11px] mt-1 block ${ok ? "text-emerald-400" : "text-amber-400"}`}>
      ~{n}/{limit} caracteres com dados reais {ok ? "· 1 SMS" : `· ${Math.ceil(n / (accents ? 67 : 153))} SMS`}
      {accents && " · evite acentos/emojis (reduzem o limite para 70)"}
    </span>
  );
}
const L = ({ label, children }) => (<label className="block"><span className="text-[11px] uppercase tracking-wider text-slate-500">{label}</span>{children}</label>);
